<template>
  <div class="js-beautify">
    <h1>JavaScript 代码美化工具</h1>
    
    <div class="beautify-container">
      <!-- 代码输入区域 -->
      <div class="section">
        <div class="section-header">
          <h3>输入 JavaScript 代码</h3>
          <div class="actions">
            <el-button size="small" @click="loadExample">加载示例</el-button>
            <el-button size="small" @click="clearInput">清空</el-button>
          </div>
        </div>
        <el-input
          v-model="inputCode"
          type="textarea"
          :rows="15"
          placeholder="请输入要格式化的 JavaScript 代码"
          @input="handleInput"
        />
      </div>

      <!-- 选项设置 -->
      <div class="options-section">
        <div class="option-group">
          <span>操作类型：</span>
          <el-radio-group v-model="actionType" @change="handleInput">
            <el-radio-button label="beautify">美化</el-radio-button>
            <el-radio-button label="minify">压缩</el-radio-button>
          </el-radio-group>
        </div>

        <div class="option-group" v-if="actionType === 'beautify'">
          <span>缩进类型：</span>
          <el-radio-group v-model="indentType" @change="handleInput">
            <el-radio-button label="space">空格</el-radio-button>
            <el-radio-button label="tab">Tab</el-radio-button>
          </el-radio-group>
          
          <template v-if="indentType === 'space'">
            <span class="ml-20">缩进大小：</span>
            <el-input-number
              v-model="indentSize"
              :min="2"
              :max="8"
              @change="handleInput"
            />
          </template>
        </div>

        <div class="option-group">
          <el-checkbox v-model="options.braces" @change="handleInput">
            括号样式保持一致
          </el-checkbox>
          <el-checkbox v-model="options.preserveNewlines" @change="handleInput">
            保留换行
          </el-checkbox>
          <el-checkbox v-model="options.removeComments" @change="handleInput">
            移除注释
          </el-checkbox>
        </div>

        <div class="option-group">
          <span>代码风格：</span>
          <el-radio-group v-model="codeStyle" @change="handleInput">
            <el-radio-button label="default">默认</el-radio-button>
            <el-radio-button label="standard">Standard</el-radio-button>
          </el-radio-group>
        </div>
      </div>

      <!-- 输出区域 -->
      <div class="section">
        <div class="section-header">
          <h3>处理结果</h3>
          <div class="actions">
            <el-button type="primary" size="small" @click="copyResult">复制结果</el-button>
          </div>
        </div>
        <el-input
          v-model="outputCode"
          type="textarea"
          :rows="15"
          readonly
          placeholder="处理后的代码将显示在这里"
        />
      </div>

      <!-- 错误提示 -->
      <div class="error-section" v-if="error">
        <el-alert
          :title="error"
          type="error"
          :closable="false"
          show-icon
        />
      </div>

      <!-- 统计信息 -->
      <div class="stats-section" v-if="outputCode">
        <div class="stat-item">
          <span>原始大小：</span>
          <span>{{ formatSize(inputCode.length) }}</span>
        </div>
        <div class="stat-item">
          <span>处理后大小：</span>
          <span>{{ formatSize(outputCode.length) }}</span>
        </div>
        <div class="stat-item" v-if="actionType === 'minify'">
          <span>压缩率：</span>
          <span>{{ compressionRatio }}%</span>
        </div>
      </div>

      <!-- 说明区域 -->
      <div class="info-section">
        <h3>功能说明</h3>
        <ul>
          <li>支持 JavaScript 代码的格式化和压缩</li>
          <li>可选择不同的代码风格（默认/Standard）</li>
          <li>自动修复常见语法错误</li>
          <li>支持 ES6+ 语法</li>
          <li>可自定义缩进和格式化选项</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, reactive } from 'vue'
import { ElMessage } from 'element-plus'
import beautify from 'js-beautify'
import { minify } from 'terser'

const inputCode = ref('')
const outputCode = ref('')
const error = ref('')
const actionType = ref('beautify')
const indentType = ref('space')
const indentSize = ref(2)
const codeStyle = ref('default')

const options = reactive({
  braces: true,
  preserveNewlines: true,
  removeComments: false
})

// 示例 JavaScript 代码
const exampleJS = `function calculateTotal(items){const total=items.reduce((sum,item)=>{return sum+item.price*item.quantity},0);const tax=total*0.1;const grandTotal=total+tax;return{subtotal:total,tax:tax,total:grandTotal}}const items=[{name:"Product 1",price:10,quantity:2},{name:"Product 2",price:15,quantity:1}];const result=calculateTotal(items);console.log(result);`

// 加载示例代码
const loadExample = () => {
  inputCode.value = exampleJS
  handleInput()
}

// 清空输入
const clearInput = () => {
  inputCode.value = ''
  outputCode.value = ''
  error.value = ''
}

// 处理输入
const handleInput = async () => {
  if (!inputCode.value) {
    outputCode.value = ''
    error.value = ''
    return
  }

  try {
    if (actionType.value === 'beautify') {
      // 美化代码
      const beautifyOptions = {
        indent_size: indentType.value === 'space' ? indentSize.value : 1,
        indent_char: indentType.value === 'space' ? ' ' : '\t',
        preserve_newlines: options.preserveNewlines,
        brace_style: options.braces ? 'collapse' : 'expand',
        keep_array_indentation: true,
        space_before_conditional: true,
        unescape_strings: false,
        jslint_happy: codeStyle.value === 'standard'
      }

      outputCode.value = beautify.js(inputCode.value, beautifyOptions)
      error.value = ''
    } else {
      // 压缩代码
      const minifyOptions = {
        compress: true,
        mangle: true,
        output: {
          comments: !options.removeComments
        }
      }

      const result = await minify(inputCode.value, minifyOptions)
      outputCode.value = result.code
      error.value = ''
    }
  } catch (err) {
    error.value = `处理失败：${err.message}`
    outputCode.value = ''
  }
}

// 复制结果
const copyResult = async () => {
  if (!outputCode.value) {
    ElMessage.warning('没有可复制的内容')
    return
  }

  try {
    await navigator.clipboard.writeText(outputCode.value)
    ElMessage.success('复制成功')
  } catch (error) {
    ElMessage.error('复制失败')
  }
}

// 格式化大小
const formatSize = (bytes) => {
  return `${bytes} 字节`
}

// 计算压缩率
const compressionRatio = computed(() => {
  if (!inputCode.value || !outputCode.value) return 0
  const ratio = ((inputCode.value.length - outputCode.value.length) / inputCode.value.length) * 100
  return Math.round(ratio)
})
</script>

<style scoped>
.js-beautify {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  margin-top: 80px;
}

.beautify-container {
  margin-top: 20px;
}

.section {
  margin: 20px 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.actions {
  display: flex;
  gap: 10px;
}

.options-section {
  margin: 20px 0;
  padding: 15px;
  background-color: #f5f7fa;
  border-radius: 8px;
}

.option-group {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0;
  flex-wrap: wrap;
}

.error-section {
  margin: 20px 0;
}

.stats-section {
  display: flex;
  gap: 20px;
  margin: 20px 0;
  padding: 15px;
  background-color: #f5f7fa;
  border-radius: 8px;
}

.stat-item {
  display: flex;
  gap: 5px;
}

.info-section {
  margin-top: 30px;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 8px;
}

.info-section ul {
  padding-left: 20px;
  margin: 10px 0;
}

.info-section li {
  margin: 8px 0;
  line-height: 1.5;
}

.ml-20 {
  margin-left: 20px;
}

:deep(.el-textarea__inner) {
  font-family: monospace;
  font-size: 14px;
  line-height: 1.5;
}
</style> 